import { Doughnut } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { doughnutCenterTextPlugin, doughnutOptions } from "../utilities";
import { useAppContext } from "../../../AppProvider";
import { useShipmentsStatisticsContext } from "../providers/ShipmentsStatistics";
import { Carrier } from "../../../Models/Carrier";
import { CarrierService } from "../../../Models/CarrierService";
import { transformStringToColor } from "../../../utilities/utilities";
import { faLoader } from "../../../utilities/fontawesomeIcons";
import moment from "moment";
import React, { useMemo } from "react";
import { useInView } from "react-intersection-observer";

const ShipmentsByCarrier: React.FC<{ title: string }> = ({ title }) => {
  const { dictionary } = useAppContext();
  const { data, loader, filters } = useShipmentsStatisticsContext();
  const { ref, inView } = useInView({ triggerOnce: true });

  const doughnutContent = useMemo(() => {
    if (!inView || !data?.carrierServices?.length) return null;

    const summary =
      data?.carrierServices?.reduce<
        Record<string, { carrier: Carrier; total: number }>
      >((acc, { type, total }) => {
        const { code } = (type as CarrierService).carrier;
        acc[code] = {
          carrier: (type as CarrierService).carrier,
          total: (acc[code]?.total || 0) + total,
        };
        return acc;
      }, {}) || {};

    const carriers = Object.values(summary);

    const total: number | string = carriers.length
      ? carriers.reduce((sum, { total }) => sum + total, 0)
      : "ND";

    const dataToDisplay = {
      labels: carriers.map((c) => c.carrier.name),
      datasets: [
        {
          data: carriers.map((c) => c.total),
          backgroundColor: carriers.map((c) =>
            transformStringToColor(c.carrier.code)
          ),
        },
      ],
    };
    return (
      <>
        <Doughnut
          key={total}
          className="mt-2"
          data={dataToDisplay}
          options={doughnutOptions}
          plugins={[doughnutCenterTextPlugin(total)]}
        />
        <div className="d-flex flex-wrap justify-content-center mt-2">
          {dataToDisplay.labels.map((label, index) => (
            <div key={index} className="d-flex align-items-center me-2">
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor:
                    dataToDisplay.datasets[0].backgroundColor[index],
                  marginRight: "6px",
                  borderRadius: "2px",
                }}
              ></div>
              <label className="label-5">{label}</label>
            </div>
          ))}
        </div>
      </>
    );
  }, [inView, data]);

  return (
    <div ref={ref} className="box-container text-center h-100">
      <div className="row mb-1" style={{ height: "60px" }}>
        <div className="col">
          <label className="label-4 lh-1 mb-1">{title}</label>
          <label className="label-2">
            {moment(filters.start).isSame(moment(filters.end), "day")
              ? moment(filters.start).format("DD/MM/YYYY")
              : moment(filters.start).format("DD/MM/YYYY") +
                " - " +
                moment(filters.end).format("DD/MM/YYYY")}
          </label>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          {loader ? (
            <FontAwesomeIcon icon={faLoader} spin size="4x" />
          ) : data?.carrierServices ? (
            doughnutContent
          ) : (
            <label className="label-3">
              {dictionary.messages.no_results_found}
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipmentsByCarrier;
